import { useId, useState } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { FormField } from '@noor/ui';
import { cn } from '@noor/lib/cn';
import type { BranchFormValues } from '../model/schema';
import { CoordinatesRow } from './coordinates-row';
import { AddressSearchInput, type PickedAddress } from './address-search-input';
import { LocationPickerDialog } from './location-picker-dialog';

interface AddressFieldProps {
  label: string;
  placeholder: string;
  pickLabel: string;
  fieldClassName: string;
  /** Layout classes for the field group — the modal sizes it per column. */
  className?: string;
}

/**
 * The «Адрес» row (Figma 840:34908): one pill holding the address input and an
 * inline «Указать на карте» action.
 *
 * The action opens the address picker as its OWN dialog (`LocationPickerDialog`)
 * stacked over the wizard — Radix nests dialogs via portals without extra
 * wiring, and a dedicated dialog is what lets the picker carry a real search
 * box, zoom controls and an explicit "Ok" without cramping the wizard step.
 *
 * Coordinates and address text are only written to the form once the dialog is
 * confirmed — see `LocationPickerDialog`'s `onConfirm`.
 *
 * The input is a search-as-you-type autocomplete (`AddressSearchInput`):
 * typing lists geocoder matches and choosing one writes the address text AND
 * its coordinates. Free text is never committed — an address is only meaningful
 * paired with the point it resolves to — so blur reverts the box to the last
 * committed value.
 *
 * The picked point renders under the field (`CoordinatesRow`), where it is
 * copyable — otherwise the coordinates the picker wrote would be invisible.
 */
export function AddressField({
  label,
  placeholder,
  pickLabel,
  fieldClassName,
  className,
}: AddressFieldProps) {
  const { control, setValue } = useFormContext<BranchFormValues>();
  const address = useWatch({ control, name: 'address' });
  const latitude = useWatch({ control, name: 'latitude' });
  const longitude = useWatch({ control, name: 'longitude' });
  const [isPicking, setIsPicking] = useState(false);
  const inputId = useId();
  // The pill's border now lives on the WRAPPER, so the invalid state has to be
  // read here too — the input's own red border is no longer drawn.
  const { errors } = useFormState({ control, name: 'address' });
  const isInvalid = errors.address !== undefined;

  // Coordinates first: `address`'s cross-field validation (the "place the
  // branch" check) reads latitude/longitude, so writing it last is what lets
  // that check see the NEW point rather than the stale one still in form state.
  const commit = (result: PickedAddress) => {
    setValue('latitude', result.latitude, { shouldValidate: true });
    setValue('longitude', result.longitude, { shouldValidate: true });
    setValue('address', result.address, { shouldValidate: true, shouldDirty: true });
  };

  return (
    <FormField name="address" className={className ?? 'gap-1.5'}>
      <FormField.Label
        htmlFor={inputId}
        className="flex items-center gap-px px-1 text-sm font-medium tracking-tight"
      >
        {label}
      </FormField.Label>

      {/* The action sits INSIDE the pill (Figma 840:34910), so the capsule's
          border is drawn here and the input is stripped to bare text. A flex
          row rather than an absolutely-positioned overlay: the action then
          reserves its own width, so a longer translation of «Указать на карте»
          shortens the input instead of running underneath it. */}
      {/* `cn` (not template concatenation): the invalid border has to BEAT the
          `border-input` in `fieldClassName`, and tailwind-merge is what drops
          the losing class rather than leaving both for the cascade to settle
          by stylesheet order — which the base colour would win. */}
      {/* `border` (the WIDTH) is stated here, not left to `fieldClassName`.
          That string is written for `Input`, whose own base classes already
          carry `border border-transparent` — it only overrides the COLOUR
          (`border-input`). A plain `div` has no such base, so the colour alone
          rendered a 0-width border and the pill came out edgeless beside the
          name field it is supposed to match. */}
      <div
        className={cn(
          'relative flex items-center gap-2 border focus-within:border-input-border-focus',
          fieldClassName,
          isInvalid && 'border-destructive',
        )}
      >
        {/* `h-auto` + `p-0` collapsed the box to the text's own line box, so
            the caret rendered as a ~2px tick that was hard to even find. A
            real line-height gives it something to be as tall as, and the pill
            keeps its shape because the wrapper owns the padding. */}
        {/* `text-ellipsis` because an address routinely outruns this half of
            the row («15-mavze, Chilonzor dahasi, Uchtepa tumani, Toshkent» in
            a ~340px column). `min-w-0 flex-1` already stops the input from
            pushing the action out of the pill, but an `<input>` CLIPS its
            overflow with no marker of its own — the text simply stopped
            mid-word against the action, reading as a layout collision rather
            than as a value too long to show. The ellipsis is what makes it
            legible as "there is more here".

            Kept as an `<input>` rather than swapped for a truncating `<div>`:
            `FormField.Input` is what wires the value, the label association
            and the error `aria-*`, and the full address stays selectable and
            scrollable inside the field for anyone who needs to read it all. */}
        <AddressSearchInput
          id={inputId}
          value={address}
          invalid={isInvalid}
          className="h-9 min-w-0 flex-1 border-none bg-transparent p-0 text-sm leading-9 text-ellipsis shadow-none outline-none focus-visible:border-none focus-visible:ring-0"
          placeholder={placeholder}
          onSelect={commit}
        />
        <button
          type="button"
          onClick={() => setIsPicking(true)}
          className="shrink-0 text-sm font-medium tracking-tight text-primary underline transition-colors outline-none hover:cursor-pointer hover:text-primary-hover focus-visible:ring-[3px] focus-visible:ring-ring/40"
        >
          {pickLabel}
        </button>
      </div>

      <CoordinatesRow latitude={latitude} longitude={longitude} />

      <FormField.Message className="min-h-0 px-1 empty:hidden" />

      <LocationPickerDialog
        open={isPicking}
        onOpenChange={setIsPicking}
        address={address}
        latitude={latitude}
        longitude={longitude}
        onConfirm={commit}
      />
    </FormField>
  );
}
